/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication.service';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DiskService, OFFLINE_DATA_COUNT_ENTITY_NAME } from '../disk/disk.service';
import { UIService } from '../ui/ui.service';
import { DeviceService } from '../device/device.service';
import { NotificationService } from '../notification/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Endpoints } from 'src/config/endpoints.config';
import { Guid } from 'typescript-guid';

@Injectable({
  providedIn: 'root',
})
export class UserTagService {

  public accountTags: UserTag[] = [];
  public contactTags: UserTag[] = [];
  public PresentationTags: UserTag[] = [];
  public resourceTag: UserTag[] = [];
  private userTagDeleted = new BehaviorSubject<boolean>(false);
  public userTagDeletedObserver = this.userTagDeleted.asObservable();

  constructor(
    private authService: AuthenticationService,
    private dynamics: DynamicsClientService,
    private disk: DiskService,
    private readonly uiService: UIService,
    private readonly deviceService: DeviceService,
    private readonly toast: NotificationService,
    private readonly translate: TranslateService,
    private http: HttpClient,
  ) {

  }

  async createOrUpdateTag(tagData: UserTag, create: boolean = false): Promise<any> {
    await this.uiService.displayLoader();
    // tagData.indskr_usertagid = Guid.create().toString();
    return new Promise(async (resolve, reject) => {
      tagData.pendingPushToDynamics = true;
      if (create) {
        tagData.indskr_externalid = `offlineUserTag_${new Date().getTime()}`;
        if (!tagData.indskr_usertagid) {
          tagData.indskr_name = uniqueTagName(tagData.indskr_name, this.tagsOfEntity(tagData.entity));
        }
      }
      tagData.stateCode = TagStateCode.Active
      console.log("requestObj", tagData)
      if (this.deviceService.isOffline) {
        this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.USER_TAG, 1);
        await this.disk.upsertUserTag(tagData);
        await this.uiService.dismissLoader();
        this.toastNotificationForTags(tagData, create);
        resolve([{
          indskr_usertagid: tagData.indskr_usertagid,
          indskr_externalid: tagData.indskr_externalid
        }]);
      } else {
        await this.saveTagOnline(tagData).subscribe(async response => {
          response.forEach(async res => {
            tagData.pendingPushToDynamics = false;
            await this.disk.upsertUserTag(tagData);
            if (tagData.indskr_externalid === res.indskr_externalid) {
              tagData.indskr_usertagid = res.indskr_usertagid;
              this.toastNotificationForTags(tagData, create);
              if (res["indskr_usertagid"]) {
                await this.uiService.dismissLoader();
                resolve(response);
              } else {
                await this.uiService.dismissLoader();
                reject(response);
              }
            }
          });
        }, async error => {
          console.log("Fails to create tag due to", error);
          tagData.pendingPushToDynamics = true;
          this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.USER_TAG, 1);
          await this.disk.upsertUserTag(tagData);
          await this.uiService.dismissLoader();
          // error toast notification
          reject(error);
        });
      }
    });
  }

  private tagsOfEntity(entity: TagEntityType): UserTag[] {
    switch (entity) {
      case TagEntityType.ACCOUNT: return this.accountTags;
      case TagEntityType.PRESENTATION: return this.PresentationTags;
      case TagEntityType.RESOURCE: return this.resourceTag;
      default: return this.contactTags;
    }
  }

  private toastNotificationForTags(tagData: UserTag, create: boolean, deleteTag = false) {
    if (deleteTag) {
      this.toast.notify(`'${tagData.indskr_name}' ${this.translate.instant("DELETED_SUCCESSFULLY")}`, "");
      return;
    }
    if (create) {
      this.toast.notify(`'${tagData.indskr_name}' ${this.translate.instant("SAVED_SUCCESSFULLY")}`, "");
    } else {
      this.toast.notify(`'${tagData.indskr_name}' ${this.translate.instant("UPDATED_SUCCESSFULLY")}`, "");
    }
  }

  private saveTagOnline(reqBody: UserTag): Observable<any> {
    if (this.deviceService.isOffline) {
      if (reqBody[0].pendingPushToDynamics) {
        this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.USER_TAG, 1);
        reqBody[0].pendingPushToDynamics = true;
      }
    } else {
      let headers = new HttpHeaders();
      let payload;
      if (reqBody.entity == TagEntityType.ACCOUNT) {
        payload = {
          indskr_externalid: reqBody.indskr_externalid,
          indskr_usertagid: reqBody.indskr_usertagid,
          deleted: reqBody.deleted,
          indskr_name: reqBody.indskr_name,
          accounts: reqBody.entityRecords ? reqBody.entityRecords.map(a => { return { 'accountId': a.id, 'deleted': a.deleted } }) : [],
          pendingPushToDynamics: reqBody.pendingPushToDynamics,
          stateCode: reqBody.stateCode,
          indskr_taggedfor: 'ACCOUNT',
        }
      } else if (reqBody.entity == TagEntityType.CONTACT) {
        payload = {
          indskr_externalid: reqBody.indskr_externalid,
          indskr_usertagid: reqBody.indskr_usertagid,
          deleted: reqBody.deleted,
          indskr_name: reqBody.indskr_name,
          contacts: reqBody.entityRecords ? reqBody.entityRecords.map(a => { return { 'contactId': a.id, 'deleted': a.deleted } }) : [],
          pendingPushToDynamics: reqBody.pendingPushToDynamics,
          stateCode: reqBody.stateCode,
          indskr_taggedfor: 'CONTACT',
        }
      }
       else if (reqBody.entity == TagEntityType.PRESENTATION) {
        payload = {
          indskr_externalid: reqBody.indskr_externalid,
          indskr_usertagid: reqBody.indskr_usertagid,
          deleted: reqBody.deleted,
          indskr_name: reqBody.indskr_name,
          presentations: reqBody.entityRecords ? reqBody.entityRecords.map(a => { return { 'presentationId': a.id, 'deleted': a.deleted } }) : [],
          pendingPushToDynamics: reqBody.pendingPushToDynamics,
          stateCode: reqBody.stateCode,
          indskr_taggedfor: 'Presentation',
          entity:reqBody.entity
        }
      }
       else if (reqBody.entity == TagEntityType.RESOURCE) {
        payload = {
          indskr_externalid: reqBody.indskr_externalid,
          indskr_usertagid: reqBody.indskr_usertagid,
          deleted: reqBody.deleted,
          indskr_name: reqBody.indskr_name,
          resource: reqBody.entityRecords ? reqBody.entityRecords.map(a => { return { 'resourceId': a.id, 'deleted': a.deleted, 'type': a.type } }) : [],
          pendingPushToDynamics: reqBody.pendingPushToDynamics,
          stateCode: reqBody.stateCode,
          indskr_taggedfor: 'RESOURCE',
          entity:reqBody.entity
        }
      }
      let url: string = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.contacts.CONTACT_TAGGING;
      return this.http.post(url, [payload], { headers });
    }
  }

  async deleteTag(userTagPayload: UserTag) {
    await this.uiService.displayLoader();
    if (this.deviceService.isOffline) {
      
      if (userTagPayload.entity == TagEntityType.ACCOUNT) {
        // offline create and offline delete
        if (!userTagPayload.indskr_usertagid) {
          await this.disk.remove(DB_KEY_PREFIXES.USER_TAG + userTagPayload.indskr_externalid)
        } else {
          // online create and offline delete
          userTagPayload.pendingPushToDynamics = true;
          this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.USER_TAG, 1);
          await this.disk.upsertUserTag(userTagPayload);
        }
        if(this.accountTags.length){
          let selIndex = this.accountTags.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
          if (selIndex >= 0) {
            this.accountTags.splice(selIndex, 1);
          }
        }
      } else if (userTagPayload.entity == TagEntityType.CONTACT) {
        // offline create and offline delete
        if (!userTagPayload.indskr_usertagid) {
          await this.disk.remove(DB_KEY_PREFIXES.CONTACT_TAG + userTagPayload.indskr_externalid)
        } else {
          // online create and offline delete
          userTagPayload.pendingPushToDynamics = true;
          this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.CONTACT_TAG, 1);
          await this.disk.upsertUserTag(userTagPayload);
        }
        if(this.contactTags.length){
          let selIndex = this.contactTags.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
          if (selIndex >= 0) {
            this.contactTags.splice(selIndex, 1);
          }
        }
      } else if (userTagPayload.entity == TagEntityType.PRESENTATION) {
        // offline create and offline delete
        if (!userTagPayload.indskr_usertagid) {
          await this.disk.remove(DB_KEY_PREFIXES.USER_TAG + userTagPayload.indskr_externalid)
        } else {
          // online create and offline delete
          userTagPayload.pendingPushToDynamics = true;
          this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.USER_TAG, 1);
          await this.disk.upsertUserTag(userTagPayload);
        }
        if(this.PresentationTags.length){
          let selIndex = this.PresentationTags.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
          if (selIndex >= 0) {
            this.PresentationTags.splice(selIndex, 1);
          }
        }
      } else if (userTagPayload.entity == TagEntityType.RESOURCE) {
        // offline create and offline delete
        if (!userTagPayload.indskr_usertagid) {
          await this.disk.remove(DB_KEY_PREFIXES.USER_TAG + userTagPayload.indskr_externalid)
        } else {
          // online create and offline delete
          userTagPayload.pendingPushToDynamics = true;
          this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.USER_TAG, 1);
          await this.disk.upsertUserTag(userTagPayload);
        }
        if(this.resourceTag.length){
          let selIndex = this.resourceTag.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
          if (selIndex >= 0) {
            this.resourceTag.splice(selIndex, 1);
          }
        }
      }
      this.toastNotificationForTags(userTagPayload, false, true);
      await this.uiService.dismissLoader();
    } else {
      await this.saveTagOnline(userTagPayload).subscribe(async response => {
        console.log(response);
        if (response) {
          userTagPayload.pendingPushToDynamics = false;
          if (userTagPayload.entity == TagEntityType.ACCOUNT) {
            await this.disk.remove(DB_KEY_PREFIXES.USER_TAG + userTagPayload.indskr_externalid);
            let selIndex = this.accountTags.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
            if (selIndex >= 0) {
              this.accountTags.splice(selIndex, 1);
            }
          } else if (userTagPayload.entity == TagEntityType.CONTACT) {
            await this.disk.remove(DB_KEY_PREFIXES.CONTACT_TAG + userTagPayload.indskr_externalid);
            let selIndex = this.contactTags.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
            if (selIndex >= 0) {
              this.contactTags.splice(selIndex, 1);
            }
          }else if (userTagPayload.entity == TagEntityType.PRESENTATION) {
            await this.disk.remove(DB_KEY_PREFIXES.USER_TAG + userTagPayload.indskr_externalid);
            let selIndex = this.PresentationTags.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
            if (selIndex >= 0) {
              this.PresentationTags.splice(selIndex, 1);
            }
          }else if (userTagPayload.entity == TagEntityType.RESOURCE) {
            await this.disk.remove(DB_KEY_PREFIXES.USER_TAG + userTagPayload.indskr_externalid);
            let selIndex = this.resourceTag.findIndex(tag => userTagPayload.indskr_externalid === tag.indskr_externalid);
            if (selIndex >= 0) {
              this.resourceTag.splice(selIndex, 1);
            }
          }

          this.toastNotificationForTags(userTagPayload, false, true);
          this.userTagDeleted.next(true);
          await this.uiService.dismissLoader();
        } else {
          await this.uiService.dismissLoader();
        }
      });
    }
  }

  uploadOfflineData(tagDataList: UserTag[]): Observable<any> {
    let headers = new HttpHeaders();
    let url: string = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.contacts.CONTACT_TAGGING;
    let payload = [];
    tagDataList.forEach(item => {
      if (item.entity == TagEntityType.ACCOUNT) {
        payload.push({
          indskr_externalid: item.indskr_externalid,
          indskr_usertagid: item.indskr_usertagid,
          deleted: item.deleted,
          indskr_name: item.indskr_name,
          accounts: item.entityRecords ? item.entityRecords.map(a => { return { 'accountId': a.id, 'deleted': a.deleted } }) : [],
          pendingPushToDynamics: item.pendingPushToDynamics,
          stateCode: item.stateCode,
          indskr_taggedfor: 'ACCOUNT',
        });
      } else if (item.entity == TagEntityType.CONTACT) {
        payload.push({
          indskr_externalid: item.indskr_externalid,
          indskr_usertagid: item.indskr_usertagid,
          deleted: item.deleted,
          indskr_name: item.indskr_name,
          contacts: item.entityRecords ? item.entityRecords.map(a => { return { 'contactId': a.id, 'deleted': a.deleted } }) : [],
          pendingPushToDynamics: item.pendingPushToDynamics,
          stateCode: item.stateCode,
          indskr_taggedfor: 'CONTACT',
        });
      } else  if (item.entity == TagEntityType.PRESENTATION) {
        payload.push({
          indskr_externalid: item.indskr_externalid,
          indskr_usertagid: item.indskr_usertagid,
          deleted: item.deleted,
          indskr_name: item.indskr_name,
          presentations: item.entityRecords ? item.entityRecords.map(a => { return { 'presentationId': a.id, 'deleted': a.deleted } }) : [],
          pendingPushToDynamics: item.pendingPushToDynamics,
          stateCode: item.stateCode,
          indskr_taggedfor: 'Presentation',
          entity:item.entity
        });
      } else if (item.entity == TagEntityType.RESOURCE) {
        payload.push({
          indskr_externalid: item.indskr_externalid,
          indskr_usertagid: item.indskr_usertagid,
          deleted: item.deleted,
          indskr_name: item.indskr_name,
          resource: item.entityRecords ? item.entityRecords.map(a => { return { 'resourceId': a.id, 'deleted': a.deleted, 'type': a.type } }) : [],
          pendingPushToDynamics: item.pendingPushToDynamics,
          stateCode: item.stateCode,
          indskr_taggedfor: 'RESOURCE',
          entity:item.entity
        })
      }
    });
    return this.http.post(url, payload, { headers });
  }

  public sortUserTags(tags:Array<UserTag>):Array<UserTag>{
    return tags.sort((tagA, tagB): number => {
      return (tagA.indskr_name.toLowerCase() < tagB.indskr_name.toLowerCase() ? -1 : 1);
    });
  }

}
export class UserTag {
  constructor(public indskr_externalid: string,
    public indskr_usertagid: string,
    public deleted: boolean = false,
    public indskr_name: string,
    public entityRecords: EntityTag[],
    public pendingPushToDynamics: boolean = false,
    public stateCode: number = TagStateCode.Active,
    public entity: TagEntityType = TagEntityType.CONTACT,
    public visibility: string,
    public filter: boolean = true,
    public allowUserToModifytag : boolean = false,
    public BUs: Array<string>= [],
    public Posiions: Array<string> = [],
    
  ) {
  }

}
export class EntityTag {
  constructor(
    public id: string,
    public deleted: boolean = false,
    public type?: string) {
  }
}


export enum TagStateCode {
  Active = 0,
  InActive = 1
}

export function uniqueTagName(name: string, existingTags: { indskr_name?: string }[]): string {
  const taken = new Set(existingTags.map(tag => (tag.indskr_name || '').trim().toLowerCase()));
  const base = name.trim();
  let candidate = base;
  for (let n = 1; taken.has(candidate.toLowerCase()); n++) {
    candidate = `${base} (${n})`;
  }
  return candidate;
}

export enum TagEntityType {
  ACCOUNT = 'ACCOUNT',
  CONTACT = 'CONTACT',
  PRESENTATION ='PRESENTATION',
  RESOURCE ='RESOURCE',
}

export enum TagVisibility {
  PERSONAL = '548910000',
  PUBLIC = '548910001',
  ASSIGNED = '600000000'
}
